import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Camera, Sparkles, Trash2, Edit3, X, Smile, Mail } from "lucide-react";
import api, { formatApiError, cachedGet, apiCache } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AI_PHOTO_STYLES } from "@/lib/themes";
import AvatarStudioCore from "@/components/AvatarStudioCore";
import InviteDialog from "@/components/InviteDialog";

const RATE_TYPES = ["weekday_day", "weekday_evening", "weekday_night", "sleepover", "saturday", "sunday", "public_holiday"];

export default function WorkersPage() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [workers, setWorkers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    try { await cachedGet("/workers", null, (data) => setWorkers(data)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!confirm("Delete this worker? Their timesheets remain.")) return;
    await api.delete(`/workers/${id}`);
    toast.success("Worker deleted");
    setWorkers((prev) => prev.filter((w) => w.id !== id));
    apiCache.invalidate("/workers");
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="workers-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Support Workers</h1>
          <p className="text-muted-foreground">Manage worker profiles, pay rates, and AI photos.</p>
        </div>
        {isClient && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setInviteOpen(true)} data-testid="invite-worker-btn"
              variant="outline" className="rounded-full min-h-[48px] font-bold gap-2 border-2">
              <Mail className="w-4 h-4" /> Invite by email
            </Button>
            <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-worker-btn"
              className="rounded-full min-h-[48px] font-bold gap-2" style={{ background: "hsl(var(--client))" }}>
              <Plus className="w-4 h-4" /> Add worker
            </Button>
          </div>
        )}
      </header>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        targetRole="worker"
        testIdPrefix="invite-worker"
        onSent={load}
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {workers.map((w) => (
          <WorkerCard key={w.id} w={w} isClient={isClient}
            onEdit={() => { setEditing(w); setOpen(true); }}
            onDelete={() => onDelete(w.id)}
            onChange={load} />
        ))}
      </div>

      <WorkerDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function WorkerCard({ w, isClient, onEdit, onDelete, onChange }) {
  const [photoOpen, setPhotoOpen] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(false);
  // Pick a vibrant gradient based on worker id for a unique look
  const gradients = [
    "linear-gradient(135deg, hsl(var(--client)), hsl(var(--coral)))",
    "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))",
    "linear-gradient(135deg, hsl(var(--sun)), hsl(var(--client)))",
    "linear-gradient(135deg, #7B5EA7, #EE82A2)",
    "linear-gradient(135deg, #48CAE4, #1E6091)",
    "linear-gradient(135deg, #F4A261, #E76F51)",
  ];
  const gradient = gradients[(w.id || "x").charCodeAt(0) % gradients.length];

  return (
    <Card className="rounded-3xl border-2 card-lift relative overflow-hidden" data-testid={`worker-card-${w.id}`}>
      <div className="h-20 relative" style={{ background: gradient }}>
        <div className="absolute -bottom-8 left-5">
          <Avatar className="w-16 h-16 ring-4 ring-card">
            <AvatarImage src={w.photo_url} loading="lazy" decoding="async" />
            <AvatarFallback className="bg-card text-foreground font-bold text-lg">{w.full_name?.[0]}</AvatarFallback>
          </Avatar>
        </div>
        {w.ai_photos?.length > 0 && (
          <span className="absolute top-2 right-2 rounded-full bg-white/90 text-coral px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> {w.ai_photos.length} AI
          </span>
        )}
      </div>
      <div className="p-5 pt-10">
        <h3 className="font-display font-extrabold text-lg leading-tight truncate">
          {w.full_name}{w.nickname ? <span className="text-muted-foreground font-semibold"> &ldquo;{w.nickname}&rdquo;</span> : null}
        </h3>
        <p className="text-xs text-muted-foreground truncate">{w.email}</p>
        <p className="text-xs text-muted-foreground truncate">{w.phone}</p>
        <div className="mt-3 grid grid-cols-2 gap-1.5 text-xs">
          <Badge variant="outline" className="rounded-full justify-start">ABN {w.abn || "—"}</Badge>
          <Badge variant="outline" className="rounded-full justify-start">BSB {w.bsb || "—"}</Badge>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="rounded-full font-bold border-2" onClick={onEdit} data-testid={`edit-worker-${w.id}`}>
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          {isClient && (
            <Button size="sm" variant="outline" className="rounded-full font-bold border-2" onClick={() => setRatesOpen(true)} data-testid={`rates-worker-${w.id}`}>
              💰 Pay rates
            </Button>
          )}
          <Button size="sm" className="rounded-full font-bold gap-1 text-white" style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }}
            onClick={() => setPhotoOpen(true)} data-testid={`photo-worker-${w.id}`}>
            <Sparkles className="w-3.5 h-3.5" /> AI Studio
          </Button>
          {isClient && (
            <Button size="sm" variant="ghost" className="rounded-full text-destructive font-bold ml-auto" onClick={onDelete} data-testid={`delete-worker-${w.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      <PhotoDialog open={photoOpen} onOpenChange={setPhotoOpen} worker={w} onChange={onChange} />
      <RatesDialog open={ratesOpen} onOpenChange={setRatesOpen} worker={w} onChange={onChange} />
    </Card>
  );
}

function WorkerDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ full_name: "", nickname: "", email: "", phone: "", address: "", bank_name: "", bsb: "", bank_account: "", abn: "", intro: "" });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    setForm(editing || { full_name: "", nickname: "", email: "", phone: "", address: "", bank_name: "", bsb: "", bank_account: "", abn: "", intro: "" });
  }, [editing, open]);

  const save = async () => {
    try {
      if (editing) await api.put(`/workers/${editing.id}`, form);
      else await api.post("/workers", form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-3xl" data-testid="worker-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit worker" : "Add support worker"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field id="full_name" label="Full name" v={form.full_name} oc={upd("full_name")} />
          <Field id="nickname" label="Nickname (optional)" v={form.nickname} oc={upd("nickname")} />
          <Field id="email" label="Email" v={form.email} oc={upd("email")} type="email" />
          <Field id="phone" label="Phone" v={form.phone} oc={upd("phone")} />
          <Field id="abn" label="ABN" v={form.abn} oc={upd("abn")} />
          <div className="sm:col-span-2"><Field id="bank_name" label="Bank name (e.g. Commonwealth Bank)" v={form.bank_name} oc={upd("bank_name")} /></div>
          <Field id="bsb" label="BSB" v={form.bsb} oc={upd("bsb")} />
          <Field id="bank_account" label="Bank account" v={form.bank_account} oc={upd("bank_account")} />
          <div className="sm:col-span-2"><Field id="address" label="Address" v={form.address} oc={upd("address")} /></div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="intro" className="text-xs uppercase tracking-wider font-bold">Intro for cover letter</Label>
            <Textarea id="intro" data-testid="field-intro" rows={3} value={form.intro} onChange={upd("intro")} className="rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full font-bold" data-testid="worker-cancel-btn">Cancel</Button>
          <Button onClick={save} className="rounded-full font-bold" style={{ background: "hsl(var(--client))" }} data-testid="worker-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, label, v, oc, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs uppercase tracking-wider font-bold">{label}</Label>
      <Input id={id} value={v || ""} onChange={oc} type={type} className="min-h-[48px] rounded-xl" data-testid={`field-${id}`} />
    </div>
  );
}

function RatesDialog({ open, onOpenChange, worker, onChange }) {
  const [rates, setRates] = useState({});
  useEffect(() => { setRates(worker?.pay_rates || {}); }, [worker, open]);

  const save = async () => {
    try {
      await api.put(`/workers/${worker.id}/payrates`, rates);
      toast.success("Pay rates updated");
      onChange(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="rates-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Pay rates — {worker?.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          {RATE_TYPES.map((rt) => (
            <div key={rt} className="grid grid-cols-[1fr,120px] items-center gap-3">
              <Label className="font-semibold capitalize">{rt.replace(/_/g, " ")}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">$</span>
                <Input type="number" step="0.5" value={rates[rt] ?? ""} onChange={(e) => setRates((r) => ({ ...r, [rt]: parseFloat(e.target.value) || 0 }))}
                  className="pl-7 rounded-xl text-right" data-testid={`rate-${rt}`} />
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full font-bold" style={{ background: "hsl(var(--client))" }} data-testid="rates-save-btn">Save rates</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoDialog({ open, onOpenChange, worker, onChange }) {
  const [tab, setTab] = useState("photo");
  const [prompt, setPrompt] = useState("Cartoon style, warm pastel colours, friendly portrait");
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(worker?.photo_url || "");
  const [history, setHistory] = useState(worker?.ai_photos || []);
  useEffect(() => {
    setPhoto(worker?.photo_url || "");
    setHistory(worker?.ai_photos || []);
  }, [worker, open]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/workers/${worker.id}/photo`, { image_b64: reader.result });
        setPhoto(reader.result);
        toast.success("Photo uploaded");
        onChange();
      } catch (err) { toast.error(formatApiError(err)); }
    };
    reader.readAsDataURL(file);
  };

  const aiEdit = async (overridePrompt) => {
    if (!photo) { toast.error("Upload a photo first"); return; }
    const used = overridePrompt || prompt;
    setBusy(true);
    try {
      const { data } = await api.post(`/workers/${worker.id}/photo/ai-edit`, { prompt: used });
      setPhoto(data.url);
      setHistory((h) => [{ prompt: used, url: data.url, at: new Date().toISOString() }, ...h]);
      toast.success("AI photo created");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setAsMain = async (url) => {
    await api.post(`/workers/${worker.id}/photo`, { image_b64: url });
    setPhoto(url); toast.success("Set as main photo"); onChange();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="photo-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-coral" /> AI Studio — {worker?.full_name}</DialogTitle></DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-full p-1 h-12">
            <TabsTrigger value="photo" className="rounded-full gap-1.5" data-testid="ws-tab-photo"><Camera className="w-4 h-4" /> Real photo</TabsTrigger>
            <TabsTrigger value="avatar" className="rounded-full gap-1.5" data-testid="ws-tab-avatar"><Smile className="w-4 h-4" /> Fun avatar</TabsTrigger>
          </TabsList>

          <TabsContent value="photo" className="space-y-4 mt-4">
            <div className="rounded-2xl border-2 border-dashed p-3 grid place-items-center bg-muted/30 min-h-[260px] relative overflow-hidden">
              {photo ? (
                <>
                  <img src={photo} alt="worker" className="rounded-xl max-h-[320px]" data-testid="worker-photo-preview" />
                  {busy && <div className="absolute inset-0 bg-black/50 grid place-items-center text-white"><div className="text-center"><div className="w-10 h-10 rounded-full border-4 border-white border-t-transparent animate-spin mx-auto" /><p className="mt-2 text-sm font-bold">Cooking the AI magic…</p></div></div>}
                </>
              ) : <p className="text-sm text-muted-foreground">No photo yet — upload one below to start.</p>}
            </div>
            <Input type="file" accept="image/*" onChange={upload} className="rounded-xl" data-testid="photo-upload-input" />
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">One-tap AI styles</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {AI_PHOTO_STYLES.map((st) => (
                  <Button key={st.id} variant="outline" disabled={busy || !photo} onClick={() => aiEdit(st.prompt)}
                    data-testid={`ai-style-${st.id}`}
                    className="rounded-2xl h-auto py-3 flex flex-col gap-1 border-2 hover:border-coral/50 hover:bg-coral/5">
                    <span className="text-2xl">{st.emoji}</span>
                    <span className="font-bold text-xs">{st.label}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold">Or your own prompt</Label>
              <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} className="rounded-xl" data-testid="ai-prompt-input" />
              <Button onClick={() => aiEdit()} disabled={busy || !photo} className="w-full rounded-full font-bold min-h-[48px] gap-2" data-testid="ai-edit-btn"
                style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))", color: "white" }}>
                <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate AI photo"}
              </Button>
            </div>
            {history.length > 0 && (
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">AI gallery</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {history.map((h, idx) => (
                    <button key={idx} type="button" onClick={() => setAsMain(h.url)}
                      className="relative group rounded-xl overflow-hidden border-2 hover:border-client" data-testid={`history-photo-${idx}`}>
                      <img src={h.url} alt={h.prompt} className="w-full aspect-square object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 grid place-items-center transition-colors">
                        <span className="opacity-0 group-hover:opacity-100 text-white text-[10px] font-bold">Use as main</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="avatar" className="mt-4">
            <p className="text-sm text-muted-foreground mb-3">Pick a fun avatar separate from the real photo. Shown in the worker card and dashboards.</p>
            <AvatarStudioCore
              endpoints={{
                put: `/workers/${worker?.id}/avatar`,
                aiEdit: `/workers/${worker?.id}/avatar/ai-edit`,
                aiGenerate: `/workers/${worker?.id}/avatar/ai-generate`,
              }}
              currentUrl={worker?.avatar_url || ""}
              seedName={worker?.full_name || "Worker"}
              onChange={onChange}
              compact={false}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
